import { pinnedConversationComparator } from '../../lib/pinOrder';
import type { ReactNode } from 'react';
import type { PageFilterField } from 'elowen-plugin-ui-kit';
import type { ConversationArchiveState, BrainConversationIcon, BrainConversationMembership, BrainStreamControl, ConversationLiveUsage, ConversationProjectIdentity, Profile, ProjectExecutionRef } from '../../../src/shared/wireContract';
import type { BrainActivityView, BrainSearchHit, ConversationSubagentNode, Project } from '../../lib/types';
import { sortConversationTree, type ConversationRow, type ConversationTreeNode } from '../../lib/conversationTree';

export type ConversationListView = 'mine' | 'all';
export type ConversationListStatus = 'working' | 'waiting' | 'unread' | 'error';
export interface ConversationListFilters {
  projects: string[];
  statuses: ConversationListStatus[];
  models: string[];
  types: string[];
  owners: number[];
}
export type ConversationListSortKey = 'updated' | 'tokens' | 'title' | 'state' | 'model' | 'owner';
export interface ConversationListSort { key: ConversationListSortKey; direction: 'asc' | 'desc' }
/** Members and the viewer's actions come from the listing (`BrainConversationMembership`); the controller
 *  narrows them only where this browser knows more (pin needs the personal row, oversight deletes). */
export interface ConversationListItem extends ConversationRow, ConversationArchiveState, BrainConversationIcon, BrainConversationMembership,
  Pick<BrainStreamControl, 'programStatus' | 'backgroundWorking'> {
  ownerId: number;
  provider?: string;
  cost?: number;
  /** The live measurement of a WORKING conversation, carried by the daemon's list payload. Absent for a
   *  settled conversation: a settled row has no live measurement to show. */
  liveUsage?: ConversationLiveUsage;
  projectRef: ProjectExecutionRef | null;
  project: Project | null;
  /** The daemon's name for the conversation's Project, from the personal listing: a guest's shared row
   *  works in a Project that is not among the guest's own, so `project` stays null there. */
  projectIdentity?: ConversationProjectIdentity;
  projectUnavailable: boolean;
  activity?: Omit<BrainActivityView, 'unread'> & { unread?: boolean };
  unread: boolean | null;
  pinnedAt: string | null;
}
export type ConversationListSectionKey = 'unread' | 'pinned' | 'today' | 'yesterday' | 'thisWeek' | 'older' | 'results';
export interface ConversationListSection {
  key: ConversationListSectionKey;
  total: number;
  roots: ConversationTreeNode[];
}
export type ConversationListReadState =
  | { status: 'loading' }
  | { status: 'error'; error: unknown; retry: () => void }
  | { status: 'ready' };
export type ConversationListExportFormat = 'html' | 'jsonl';
export interface ConversationListActions {
  open: (item: ConversationListItem) => void;
  rename: (item: ConversationListItem, title: string) => Promise<boolean>;
  fork: (item: ConversationListItem) => void;
  pin: (item: ConversationListItem) => void;
  archive: (item: ConversationListItem) => void;
  archiveHeld: (id: string) => boolean;
  mute: (item: ConversationListItem) => void;
  mutePending: (id: string) => boolean;
  export: (item: ConversationListItem, format: ConversationListExportFormat) => void;
  delete: (item: ConversationListItem) => void;
  /** A guest asks to leave; the panel confirms before the controller writes. */
  leave: (item: ConversationListItem) => void;
  showSubagents: (item: ConversationListItem) => void;
}
export interface ConversationListRowProps {
  item: ConversationListItem;
  depth: number;
  active: boolean;
  selected: boolean;
  selectionMode: boolean;
  selectable: boolean;
  busy: boolean;
  contextOnly: boolean;
  /** The delete of this row has an unknown outcome and is not offered again. */
  deleteHeld?: boolean;
  expanded: boolean;
  hasBranch: boolean;
  branchId?: string;
  snippet?: BrainSearchHit;
  search: string;
  now: number;
  view: ConversationListView;
  runningAgents?: { count: number; truncated: boolean };
  scheduledCount?: number;
  scheduledLabel: string;
  hasSubagents: boolean;
  actions: ConversationListActions;
  onToggle: () => void;
  onSelect: () => void;
  onRenameChange?: (editing: boolean) => void;
}
export interface ConversationListProps {
  sections: readonly ConversationListSection[];
  items: ReadonlyMap<string, ConversationListItem>;
  view: ConversationListView;
  activeId: string | null;
  selected: ReadonlySet<string>;
  selectionMode: boolean;
  selectionEnabled?: boolean;
  busy: boolean;
  contextIds: ReadonlySet<string>;
  /** Rows whose delete answer was lost: neither selectable nor deletable until the listing is read back. */
  heldIds?: ReadonlySet<string>;
  expanded: ReadonlySet<string>;
  /** The remembered fold of the pinned section; the dated sections do not fold. */
  pinnedOpen: boolean;
  onPinnedOpenChange: (open: boolean) => void;
  snippets: ReadonlyMap<string, BrainSearchHit>;
  search: string;
  now: number;
  actions: ConversationListActions;
  onToggle: (id: string) => void;
  onSelect: (id: string) => void;
  onRenameChange?: (id: string, editing: boolean) => void;
  renderBranch: (node: ConversationTreeNode, item: ConversationListItem) => ReactNode;
  runningAgents?: ReadonlyMap<string, { count: number; truncated: boolean }>;
  scheduledCounts?: ReadonlyMap<string, number>;
  scheduledLabel: string;
  subagentIds?: ReadonlySet<string>;
}
export interface ConversationListFilterOption { value: string; label: string; icon?: ReactNode }
export interface ConversationListToolbarProps {
  view: ConversationListView;
  search: string;
  onSearch: (value: string) => void;
  filters: ConversationListFilters;
  onFilters: (filters: ConversationListFilters | ((current: ConversationListFilters) => ConversationListFilters)) => void;
  options: {
    projects: readonly ConversationListFilterOption[];
    statuses: readonly ConversationListFilterOption[];
    models: readonly ConversationListFilterOption[];
    types: readonly ConversationListFilterOption[];
    owners: readonly ConversationListFilterOption[];
  };
  sort: ConversationListSort;
  onSort: (sort: ConversationListSort) => void;
  selectionMode: boolean;
  onSelectionMode: () => void;
  pageSelection: boolean | 'indeterminate';
  onSelectPage: () => void;
  busy: boolean;
  scopeField?: PageFilterField;
  newConversation: ReactNode;
  deleteAll?: ReactNode;
}

/** A conversation member as people read it, the same fallback `Avatar` uses for its label. */
export const profileName = (profile: Profile): string => profile.name.trim() || profile.username;
/** `Avatar` takes an absent picture as undefined; the wire `Profile` says null. */
export const profileAvatarUser = (profile: Profile) => ({ ...profile, avatar: profile.avatar ?? undefined });

export const EMPTY_CONVERSATION_FILTERS: ConversationListFilters = {
  projects: [], statuses: [], models: [], types: [], owners: [],
};

/** Catalog failures belong to the controller's read state, not to the unavailable-project facet. */
export function conversationProjectKey(item: ConversationListItem): string {
  if (item.projectUnavailable) return 'unavailable';
  const projectId = item.projectRef?.projectId;
  return projectId === undefined ? 'host' : `project:${projectId}`;
}

/** Encode the tuple, rather than a delimiter that either provider or model ids could contain. */
export function conversationModelKey(item: Pick<ConversationListItem, 'provider' | 'model'>): string {
  return JSON.stringify([item.provider ?? '', item.model]);
}

/** A scheduled parent retains its actual origin; scheduled activity is an overlapping category. */
export function conversationTypeKeys(item: ConversationRow, scheduledCount = 0): string[] {
  const platform = item.platform;
  // Web, CLI and a delegated child are surfaces of an owner conversation, not an origin of their own.
  const origin = platform && platform !== 'web' && platform !== 'cli' && platform !== 'subagent' ? platform
    : item.kind === 'conversation' ? 'web' : item.kind;
  return origin === 'cron' || scheduledCount > 0 ? [origin, 'scheduled'] : [origin];
}

export function matchesConversationFilters(
  item: ConversationListItem,
  filters: ConversationListFilters,
  scheduledCount = 0,
): boolean {
  const state = item.programStatus.state;
  return (filters.projects.length === 0 || filters.projects.includes(conversationProjectKey(item)))
    && (filters.models.length === 0 || filters.models.includes(conversationModelKey(item)))
    && (filters.owners.length === 0 || filters.owners.includes(item.ownerId))
    && (filters.types.length === 0 || conversationTypeKeys(item, scheduledCount).some((key) => filters.types.includes(key)))
    && (filters.statuses.length === 0 || filters.statuses.some((status) => {
      switch (status) {
        case 'working': return state === 'working' || item.backgroundWorking;
        case 'waiting': return state === 'blocked';
        case 'unread': return item.unread === true;
        case 'error': return state === 'error';
      }
    }));
}

const STATE_RANK: Record<BrainStreamControl['programStatus']['state'], number> = {
  idle: 0, working: 1, blocked: 2, done: 3, error: 4, clear: 5,
};

/** Direction affects only the chosen key. Equal values always keep newest-first/id ordering. */
export function compareConversationItems(
  a: ConversationListItem,
  b: ConversationListItem,
  sort: ConversationListSort,
): number {
  let primary: number;
  switch (sort.key) {
    case 'updated': primary = Date.parse(a.updated_at) - Date.parse(b.updated_at); break;
    case 'tokens': primary = (a.tokens ?? 0) - (b.tokens ?? 0); break;
    case 'title': primary = a.title.localeCompare(b.title); break;
    case 'model': primary = a.model.localeCompare(b.model) || (a.provider ?? '').localeCompare(b.provider ?? ''); break;
    case 'owner': primary = (a.ownerLabel ?? '').localeCompare(b.ownerLabel ?? ''); break;
    case 'state': primary = STATE_RANK[a.programStatus.state] - STATE_RANK[b.programStatus.state]; break;
  }
  return (sort.direction === 'asc' ? primary : -primary)
    || Date.parse(b.updated_at) - Date.parse(a.updated_at)
    || a.id.localeCompare(b.id);
}

/** Filter first, then section and sort the complete forest. The shared clock supplies browser-local now. */
export function buildConversationSections(
  roots: readonly ConversationTreeNode[],
  items: ReadonlyMap<string, ConversationListItem>,
  view: ConversationListView,
  sort: ConversationListSort,
  now: number,
  contextIds: ReadonlySet<string> = new Set(),
  pinOrder: readonly string[] = [],
): ConversationListSection[] {
  const sorted = sortConversationTree(roots, (a, b) => compareConversationItems(items.get(a.id)!, items.get(b.id)!, sort));
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const monday = new Date(today);
  monday.setDate(monday.getDate() - (monday.getDay() + 6) % 7);
  // The date sort is the only one that reads as a timeline, so only there do unread replies lift out of it.
  // `unread` is the viewer's own read cursor and null for a conversation they are not a member of, so
  // the All view collects nothing of other accounts.
  const newReplies = sort.key === 'updated';
  const grouped = new Map<ConversationListSectionKey, ConversationTreeNode[]>();
  for (const root of sorted) {
    const item = items.get(root.row.id)!;
    const updated = Date.parse(item.updated_at);
    const key: ConversationListSectionKey = newReplies && item.unread === true && !contextIds.has(root.row.id) ? 'unread'
      : view === 'mine' && item.pinnedAt !== null ? 'pinned'
      : sort.key !== 'updated' ? 'results'
      : updated >= today.getTime() ? 'today'
      : updated >= yesterday.getTime() ? 'yesterday'
      : updated >= monday.getTime() ? 'thisWeek' : 'older';
    const group = grouped.get(key);
    if (group) group.push(root);
    else grouped.set(key, [root]);
  }
  // Newest reply first in either date direction: it is a list to act on, not a timeline.
  grouped.get('unread')?.sort((a, b) => compareConversationItems(items.get(a.row.id)!, items.get(b.row.id)!, { key: 'updated', direction: 'desc' }));
  const pinned = grouped.get('pinned');
  const comparePins = pinnedConversationComparator(pinOrder);
  if (pinned) pinned.sort((a, b) => comparePins(items.get(a.row.id)!, items.get(b.row.id)!));
  const order: ConversationListSectionKey[] = sort.key !== 'updated' ? ['pinned', 'results']
    : sort.direction === 'desc' ? ['unread', 'pinned', 'today', 'yesterday', 'thisWeek', 'older']
    : ['unread', 'pinned', 'older', 'thisWeek', 'yesterday', 'today'];
  return order.flatMap((key) => {
    const sectionRoots = grouped.get(key);
    return sectionRoots ? [{ key, total: sectionRoots.filter((root) => !contextIds.has(root.row.id)).length, roots: sectionRoots }] : [];
  });
}

/** Slice root order, never descendants; continued headings keep the complete section's match count. */
export function pageConversationSections(
  sections: readonly ConversationListSection[],
  page: number,
  pageSize: number,
): ConversationListSection[] {
  const start = page * pageSize;
  const end = start + pageSize;
  let offset = 0;
  return sections.flatMap((section) => {
    const roots = section.roots.slice(Math.max(0, start - offset), Math.max(0, end - offset));
    offset += section.roots.length;
    return roots.length > 0 ? [{ ...section, roots }] : [];
  });
}

/** The name a sub-agent tree lists a child session under, for the read-only view it opens. */
export function subagentNodeName(nodes: readonly ConversationSubagentNode[], sessionId: string): string | undefined {
  for (const node of nodes) {
    if (node.childSessionId === sessionId) return node.name;
    const nested = subagentNodeName(node.children, sessionId);
    if (nested !== undefined) return nested;
  }
  return undefined;
}

/** The agents working in one conversation: its running sub-agents plus the conversation's own agent while
 *  its turn works (`mainAgent` is the row's session id then, undefined otherwise). The own agent joins the
 *  same identity set, so a tree that names the row's own session cannot count it twice.
 *  Invoke only for a successfully read branch; loading/error is not an exact zero. */
export function countRunningAgents(
  nodes: readonly ConversationSubagentNode[],
  mainAgent?: string,
): { count: number; truncated: boolean } {
  const sessions = new Set<string>(mainAgent === undefined ? [] : [mainAgent]);
  const records = new Set<string>();
  let truncated = false;
  const visit = (branch: readonly ConversationSubagentNode[]): void => {
    for (const node of branch) {
      truncated ||= node.truncated === true;
      if (node.kind !== 'workflow' && node.status === 'running') {
        if (node.childSessionId) sessions.add(node.childSessionId);
        else records.add(node.key);
      }
      visit(node.children);
    }
  };
  visit(nodes);
  return { count: sessions.size + records.size, truncated };
}
